import {
  createApproval,
  executeCentralPlan,
  CentralPeriodKey,
  CentralPlan,
  CentralPlanRun,
  CentralPlanTargetStatus,
  CentralRecommendation,
} from '../services/MendsVisionService';
import { describeCreateOutcome } from './createApprovalOutcome';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

export type CentralTargetResult = {
  contactId: number;
  sectorId: number;
  contactName: string;
  /** queued: entrou na fila de aprovação; executed: rotina rodou na hora; not_created/failed: nada foi criado. */
  kind: 'queued' | 'executed' | 'not_created' | 'failed';
  message: string;
  approvalId?: number;
};

const TITLES: Record<string, string> = {
  meetings_no_decision: 'Retomar após reunião sem decisão',
  meetings_no_show: 'Remarcar reunião (cliente faltou)',
};

/**
 * Executa uma recomendação "follow_up_batch": uma aprovação create_follow_up por cliente, uma de cada vez,
 * com a chave de idempotência do backend (repetir não duplica). Nada é enviado ao cliente aqui: a mensagem
 * só sai quando alguém aprova na fila (CI-03, CI-06).
 */
export async function runFollowUpBatch(
  rec: Pick<CentralRecommendation, 'findingId' | 'targets' | 'why'>,
  onProgress?: (done: number, total: number, last: CentralTargetResult) => void,
  create: typeof createApproval = createApproval,
): Promise<CentralTargetResult[]> {
  const results: CentralTargetResult[] = [];
  const title = TITLES[rec.findingId] || 'Retomar conversa (Central de Inteligência)';
  for (const t of rec.targets) {
    let result: CentralTargetResult;
    try {
      const created = await create({
        sectorId: t.sectorId,
        contactId: t.contactId,
        contactName: t.contactName,
        title,
        body: `Recomendação da Central de Inteligência: ${rec.why}`,
        suggestedAction: 'create_follow_up',
        actionKind: 'create_follow_up',
        actionPayload: '{}',
        idempotencyKey: t.idempotencyKey,
      });
      const outcome = describeCreateOutcome(created);
      result = outcome.kind === 'queued'
        ? {
            contactId: t.contactId,
            sectorId: t.sectorId,
            contactName: t.contactName,
            kind: 'queued',
            message: created.status === 'needs_info' ? 'Na fila: falta um dado para a IA escrever.' : 'Na fila de aprovação.',
            approvalId: outcome.approval.id,
          }
        : {
            contactId: t.contactId,
            sectorId: t.sectorId,
            contactName: t.contactName,
            kind: outcome.kind,
            message: outcome.message,
          };
    } catch (e: unknown) {
      result = {
        contactId: t.contactId,
        sectorId: t.sectorId,
        contactName: t.contactName,
        kind: 'failed',
        message: apiErrorMessage(e, 'Falha ao criar a aprovação.'),
      };
    }
    results.push(result);
    onProgress?.(results.length, rec.targets.length, result);
  }
  return results;
}

export function summarizeBatch(results: CentralTargetResult[]): string {
  const queued = results.filter((r) => r.kind === 'queued').length;
  const executed = results.filter((r) => r.kind === 'executed').length;
  const failed = results.length - queued - executed;
  const parts: string[] = [];
  if (queued) parts.push(`${queued} na fila de aprovação`);
  if (executed) parts.push(`${executed} feitas na hora`);
  if (failed) parts.push(`${failed} não criadas`);
  return parts.join(' · ') || 'Nada foi criado.';
}

/** Estado do "Executar plano" de uma recomendação na tela. */
export type PlanState = { running: boolean; run?: CentralPlanRun; error?: string };

export const PLAN_STATUS_LABEL: Record<CentralPlanTargetStatus, string> = {
  done: 'Feito',
  queued: 'Na fila de aprovação',
  executed: 'Feito na hora',
  skipped: 'Não feito',
  failed: 'Falhou',
};

/** O que a prévia promete: quantos passos internos, quantas aprovações e quantos clientes diferentes. */
export function planPreviewCounts(plan: CentralPlan): { internal: number; approvals: number; clients: number } {
  const internal = plan.steps.filter((s) => !s.talksToClient).reduce((n, s) => n + s.targets.length, 0);
  const approvals = plan.steps.filter((s) => s.talksToClient).reduce((n, s) => n + s.targets.length, 0);
  const clients = new Set(plan.steps.flatMap((s) => s.targets.map((t) => `${t.sectorId}:${t.contactId}`))).size;
  return { internal, approvals, clients };
}

/**
 * Executa o plano no servidor (que recalcula a recomendação e usa a chave central:plan:{recomendação}:{dia}): os passos
 * internos rodam na hora; cada mensagem ao cliente vira uma aprovação. Nada sai sem alguém aprovar na fila.
 */
export async function runCentralPlan(
  rec: Pick<CentralRecommendation, 'id'>,
  sectorIds: number[],
  period: CentralPeriodKey,
  execute: typeof executeCentralPlan = executeCentralPlan,
): Promise<CentralPlanRun> {
  return execute(sectorIds, period, rec.id);
}
